import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UI } from './ui';
import * as formService from '../../form-services/presentation-editor';
import { PreviewLayoutMode } from '../../entities/preview-layout-mode';
import { IPresentationEditorFormModel } from '../../form-services/presentation-editor-iface';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';
import { IShowAnnouncementBannerArgs } from '../../../../framework/components/bwj-announcement/props';
import { PresentationTiming } from '../../../../entities-generated/common/presentation-timing';
import { isNotNullOrWhitespace, isNullOrWhitespace } from '../../../../framework/utils';

xlationContext('Discussion plan editor component');

interface IPresentationEditorProps {
  formModel: IPresentationEditorFormModel;
  onStartPresentation?: () => void;
  onSavePresentation: () => void;
  savingPresentation: boolean;
}

const MOBILE_PHONE_WIDTH = 500;

// const subscriberId = 'Component.PresentationEditor';

export const PresentationEditor: React.FunctionComponent<IPresentationEditorProps> = (props) => {
  const { formModel, savingPresentation, onSavePresentation } = props;
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, true);
  const saveDisabled = useMemo(() => {
    return hasUnsavedChanges === false || savingPresentation;
  }, [hasUnsavedChanges, savingPresentation]);
  const [previewLayout, setPreviewLayout] = useState(PreviewLayoutMode.Paneled);
  const [showHelpContent, setShowHelpContent] = useState(false);
  const [showPreviewHelpPanel, setShowPreviewHelpPanel] = useState(false);
  const [userLayoutMode, setUserLayoutMode] = useState<PreviewLayoutMode | undefined>(undefined);
  const prevComplaint = useRef<'indeterminate'|'none'|'empty'|'untimed'|'inflexible'|'durationless'>('none');
  const [announcementArgs, setAnnouncementArgs] = useState<IShowAnnouncementBannerArgs | undefined>(undefined);
  const containerDims = useRef({ width: 0, height: 0 });
  const { showToast } = useToast();

  const onSavePresentationClicked = useCallback(() => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid) {
        onSavePresentation();
      }
    });
  }, [onSavePresentation]);

  // allow ctrl + s saving
  useEffect(() => {
    const event = 'keydown';
    const onKeyDown = (e: KeyboardEvent) => {
      if((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();

        if(saveDisabled === false) {
          onSavePresentationClicked();
        } else {
          showToast(MessageBarType.info, xlate('No changes to save'));
        }
      }
    };
    window.document.addEventListener(event, onKeyDown);

    return () => {
      window.document.removeEventListener(event, onKeyDown);
    };
  }, [saveDisabled, onSavePresentationClicked, showToast]);

  // manage complaint display
  useEffect(() => {
    const pc = prevComplaint.current;

    if(isNullOrWhitespace(formModel.content) && pc !== 'empty') {
      prevComplaint.current = 'empty';
      setAnnouncementArgs({
        messageType: MessageBarType.warning,
        message: xlate('<strong>This discussion plan has no content.</strong> To learn how to get started, click or tap the <em>Display help</em> button.'),
        dismissable: false,
        renderAsHtml: true,
      });
    } else if(isNotNullOrWhitespace(formModel.content) && formModel.timing === PresentationTiming.None && pc !== 'untimed') {
      prevComplaint.current = 'untimed';
      setAnnouncementArgs({
        messageType: MessageBarType.warning,
        message: xlate('<strong>This discussion plan has no timed sections.</strong> Without them, you miss out on one of the key benefits of using Presentual.  To learn more, click or tap the <em>Display help</em> button, and choose the <em>Timed Section</em> tab.'),
        dismissable: false,
        renderAsHtml: true,
      });
    } else if(formModel.timing === PresentationTiming.Firm && pc !== 'inflexible') {
      prevComplaint.current = 'inflexible';
      setAnnouncementArgs({
        messageType: MessageBarType.warning,
        message: xlate('<strong>This discussion plan has no flexibly timed sections.</strong> These are crucial to leading an adaptable discussion.  To learn more, click or tap the <em>Display help</em> button, and choose the <em>Timed Section</em> tab.'),
        dismissable: false,
        renderAsHtml: true,
      });
    } else if(formModel.timing === PresentationTiming.Proportional && formModel.duration === undefined && pc !== 'durationless') {
      prevComplaint.current = 'durationless';
      setAnnouncementArgs({
        messageType: MessageBarType.info,
        message: xlate('<strong>The discussion duration has not been specified yet.</strong> You can note the duration below (recommended), or just before the discussion begins.'),
        dismissable: false,
        renderAsHtml: true,
        icon: 'Timer',
      });
    } else if((formModel.timing === PresentationTiming.Flexible || (formModel.timing === PresentationTiming.Proportional && formModel.duration !== undefined)) && pc !== 'none'){
      prevComplaint.current = 'none';
      setAnnouncementArgs({
        messageType: MessageBarType.success,
        message: xlate('<strong>Looking good</strong> &mdash; you\'re on track to have a great discussion!'),
        dismissable: false,
        renderAsHtml: true,
        icon: 'Like',
      });
    }
  }, [formModel]);

  const onContainerSizeChange = (width: number, height: number) => {
    containerDims.current = { width, height };
    if(width > MOBILE_PHONE_WIDTH) {
      // use the user-chosen layout size.  If user never explicitly chose a layout, chose the best one for the user automatically
      if(userLayoutMode !== undefined) {
        setPreviewLayout(userLayoutMode);
      } else if (width > height) {
        setPreviewLayout(PreviewLayoutMode.Vertical);
      } else {
        setPreviewLayout(PreviewLayoutMode.Horizontal);
      }
    } else {
      setPreviewLayout(PreviewLayoutMode.Paneled);
    }
  };

  const onDisplayHelp = () => {
    setShowHelpContent(true);
    if(previewLayout === PreviewLayoutMode.Paneled) {
      setShowPreviewHelpPanel(true);
    }
  };

  const onDisplayPreview = () => {
    setShowHelpContent(false);
    if(previewLayout === PreviewLayoutMode.Paneled) {
      setShowPreviewHelpPanel(true);
    }
  };

  const onSetUserLayoutMode = (mode: PreviewLayoutMode | undefined) => {
    setUserLayoutMode(mode);
    if(mode !== undefined) {
      setPreviewLayout(mode);
    } else {
      const { width, height } = containerDims.current;
      if (width > height) {
        setPreviewLayout(PreviewLayoutMode.Vertical);
      } else {
        setPreviewLayout(PreviewLayoutMode.Horizontal);
      }
    }
  };

  return (
    <UI
      onSavePresentation={onSavePresentationClicked}
      onStartPresentation={props.onStartPresentation}
      onDisplayHelp={onDisplayHelp}
      onDisplayPreview={onDisplayPreview}
      saveDisabled={saveDisabled}
      showingPreviewHelpPanel={showPreviewHelpPanel}
      onOpenPreviewPanel={() => setShowPreviewHelpPanel(true)}
      onClosePreviewPanel={() => setShowPreviewHelpPanel(false)}
      showHelpContent={showHelpContent}
      previewLayout={previewLayout}
      userLayoutMode={userLayoutMode}
      formModel={formModel}
      onContainerSizeChange={onContainerSizeChange}
      onSetUserLayoutMode={onSetUserLayoutMode}
      announcementArgs={announcementArgs}
    />
  );
};
